import { connect } from 'react-redux';
import { Map } from 'immutable';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { toggleModal } from '@/actions';
import { setProviderSortOrder } from '@/actions/providers';
import { setRedirectParams } from '@/actions/search';
import { newPurchase, fetchTripsDetails, fetchQuickPurchaseByToken } from '@/actions/purchase';
import {
  setTripSortOrder,
  setTripFilter,
  setTripFilters,
  resetTripFilters,
} from '@/actions/tripFilters';
import { showTripDetails } from '@/actions/trip';
import { resetSeatBus } from '@/actions/seatsBus';
import parseTripDetails, { getTripTerminalInfo } from 'utils/trips/parseTripDetails';
import parseQueryString from 'utils/queryString/parseQueryString';
import stringifyQueryString from 'utils/queryString/stringifyQueryString';
import {
  tripsLocationByProvider,
  filterTrips,
  stopsFilter as stopsEvaluator,
} from 'utils/tripFilters';
// The A/B readers live with the caller, not inside `tripFilters`: they reach the Redux store
// singleton and the GrowthBook instance, and importing them there made the util impure.
import { isPriceWithoutFeesVariantActive } from 'utils/trips/priceWithoutFeesExperimentSort';
import { isFinalPriceVariantActive } from 'utils/trips/finalPriceExperimentReader';
import { getBaseTripDiscountPercent } from 'models/parseTrip';
import { resolveSpecialDiscountBannerProps } from 'utils/specialDiscount';
import {
  getRecommendedTripsExcludeList,
  getRecommendedTripsListBySearch,
  isRecommendedTripsEnabled,
} from 'utils/userPreferences';
import { passengersNotAvailable } from 'utils/passengers';
import { isOldSearchPassengersFormat } from 'utils/Reserbus';
import getSearchAnalyticsData from 'utils/search/getSearchAnalyticsData';
import { cloneSelectedSeatReferences } from 'utils/purchase/seatReferenceTransport';
import hasTooManyFilters from 'utils/search/hasTooManyFilters';
import listPaintsQuote from 'utils/search/listPaintsQuote';
import ProviderDeparturesComponent from './ProviderDepartures';

const isSearchPolling = (trips) =>
  !trips || trips.valueSeq().some((transportType) => transportType.get('isPolling'));

/**
 * Retrieve open ticket from the trips list
 * @param {Object} trips - List of trips
 * @param {String} [providerId] - Provider id
 * @returns {Object} - Open ticket trip
 */
const retrieveOpenTrip = (trips, providerId) => {
  if (!trips || trips.size === 0) return null;
  const openTrips = trips
    .filter((t) => t.get('openTicket') && (!providerId || t.get('lineId') === providerId))
    .sort((a, b) => a.getIn(['pricing', 'total']) - b.getIn(['pricing', 'total']));

  return openTrips.size > 0 ? openTrips.first().toJS() : null;
};

const isStopsFilterAvailable = (trips) => {
  return (
    trips &&
    trips.some((t) => stopsEvaluator(t, 'direct')) &&
    trips.some((t) => stopsEvaluator(t, 'multiple'))
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    config,
    providers,
    search,
    purchase,
    trips,
    cities,
    terminals,
    overlayView,
    tripsDetails,
    tripFilters,
    siemprePlus,
    whitelabelConfig: { features, env },
    exchange,
    userPreferences,
  } = state;
  const { match, location } = ownProps;
  const {
    departureDate,
    returnDate,
    passengers = 'A1',
    providerId: paramsProviderId,
    isOpen,
  } = match.params;
  const {
    providerName,
    providerId: queryProviderId,
    departureIsOpenTicket: rawDepartureIsOpenTicket = false,
  } = parseQueryString(location.search);

  // Convert string query param to boolean to avoid "false" being truthy
  const queryDepartureIsOpenTicket = rawDepartureIsOpenTicket === 'true';

  const providerId = paramsProviderId || queryProviderId;
  const places = { ...cities, ...terminals };
  const searchId = search.getIn(['departure', 'id']);
  const returnSearchId = search.getIn(['return', 'id']);
  const searchIsPolling = isSearchPolling(trips.get(searchId));
  const paymentPlansList = config.paymentPlans;
  const providerList = providers.getIn([searchId, 'list']);
  let provider = providerList && providerList.find((el) => el.get('id') === providerId);
  const transportType = provider && provider.get('transportType');

  // Filters section
  const sortBy = tripFilters.get('sortBy');
  const filters = tripFilters.get('filters');
  const departureTimeFilter = filters.get('departureTime');
  const stopsFilter = filters.get('stops');
  const categoriesFilter = filters.get('categories');
  const tripOptionsFilter = filters.get('tripOptions');
  const dLocationFilter = filters.get('departureLocation');
  const aLocationFilter = filters.get('arrivalLocation');

  const activeDepartureFilter = Array.isArray(departureTimeFilter)
    ? !departureTimeFilter.includes('none')
    : departureTimeFilter !== 'none';

  const activeLocationFilter = dLocationFilter !== 'none' || aLocationFilter !== 'none';
  const activeCategoriesFilter = categoriesFilter?.length;
  const activeTripOptionsFilter = tripOptionsFilter?.length;

  const activeStopsFilter =
    typeof stopsFilter === 'string' ? stopsFilter !== 'none' : !stopsFilter.includes('none');
  const activeFilters =
    activeDepartureFilter ||
    activeLocationFilter ||
    activeStopsFilter ||
    activeCategoriesFilter ||
    activeTripOptionsFilter;

  const hasDynamicPromotion = search.get('promotions') ? search.get('promotions').size > 0 : false;
  const visibleTrip = tripsDetails.get('visibleTrip');
  const couponCode = search.get('couponCode');
  const discountedTripsByCoupon = search.getIn(['discountedTripsByCoupon', couponCode], []);
  // The sort below and the product-list impression stay in control while this holds.
  const paintsQuote = listPaintsQuote(search);

  let filteredTrips;
  let tripList;
  let tripsWithoutFilters;

  const nearTerminals = search.getIn(['nearTerminals', 'origins']);
  const searchPassengers = search.get('passengers').toJS();

  const {
    ticket: { nit },
    operationNumbers,
  } = exchange;
  // The predicate `ProviderDepartures` uses.
  const isExchange = Boolean(operationNumbers?.length && nit);
  /**
   * The BOOK-168 reader for every sort below, with the exchange context bound: `filterTrips` hands
   * it only the list's gate, and an exchange never enters the final arm (D10). A bare reader would
   * let the sort evaluate GrowthBook and order by the markup while the cards stay on base. The
   * same holds while the list paints a coupon or wallet quote (`listPaintsQuote`).
   * @param {object} gate - The list's `{ hasMarkup, hasDisplayTotal }`, from `filterTrips`.
   * @returns {boolean} Whether to sort by the final price.
   */
  const readFinalPriceVariant = (gate) =>
    isFinalPriceVariantActive({ ...gate, hasMarkup: gate.hasMarkup && !paintsQuote, isExchange });

  if (features.PROVIDERS_SELECTION_ENABLED) {
    tripList = trips.getIn([searchId, 'buses', 'trips']);
    filteredTrips = filterTrips(
      tripList,
      providerId,
      sortBy,
      filters,
      nearTerminals,
      searchPassengers,
      isPriceWithoutFeesVariantActive,
      readFinalPriceVariant,
    );
    const filteredTripsId = filteredTrips.toJS().map((trip) => trip.id);
    tripsWithoutFilters = tripList?.filter(
      (trip) => !filteredTripsId.includes(trip.get('id')) && trip.get('lineId') === providerId,
    );
  } else {
    if (trips.getIn([searchId, 'buses', 'trips'])) {
      tripList = trips.getIn([searchId, 'buses', 'trips']).map((item) => item.set('type', 'bus'));
    }
    filteredTrips = filterTrips(
      tripList,
      null,
      sortBy,
      filters,
      nearTerminals,
      searchPassengers,
      isPriceWithoutFeesVariantActive,
      readFinalPriceVariant,
    );
    const filteredTripsId = filteredTrips.toJS().map((trip) => trip.id);
    tripsWithoutFilters = tripList?.filter((trip) => !filteredTripsId.includes(trip.get('id')));
    provider = Map({ name: null, id: '' });
  }

  tripsWithoutFilters = filterTrips(
    tripsWithoutFilters,
    null,
    sortBy,
    null,
    nearTerminals,
    undefined,
    isPriceWithoutFeesVariantActive,
    readFinalPriceVariant,
  ).toJS();
  let filteredTripsJS = filteredTrips.toJS();
  const openTrip = retrieveOpenTrip(filteredTrips, providerId);
  const noTrips = !filteredTrips.size || (filteredTrips.size === 1 && Boolean(openTrip));

  const busTrips = trips.getIn([searchId, 'buses', 'trips']);
  const areOpenTicketTrips = busTrips?.filter((t) => t.get('openTicket')).toJS();

  const roundTrip = !!returnDate;
  const returnRoundMethod = features.DISCOUNT_LABEL_ROUND_METHOD_ON_RETURN;

  // Special discount (Hot Sale / Festival de Viajes) banner.
  const { specialDiscountVariant, showSpecialDiscountBanner } = resolveSpecialDiscountBannerProps({
    features,
    brand: env.brand,
    tripList,
    pricingKey: roundTrip ? 'departureRoundTripPricing' : 'pricing',
  });

  const tripsForLineInfo = tripList?.size && tripList.toJS()[0];
  const discountPercent = getBaseTripDiscountPercent({
    trip: tripsForLineInfo,
    isRoundTrip: roundTrip,
    returnRoundMethod,
  });

  // Passengers searched
  /**
   * If some passenger searched is not found in the trips, this passengers is saved to notify
   * that the passengers was not found in this route
   */
  const isOldPassengersFormat = isOldSearchPassengersFormat(passengers);
  const passengersNotFound =
    (!isOldPassengersFormat &&
      features.VALID_CATEGORIES?.length > 1 &&
      passengersNotAvailable({
        neededPassengers: searchPassengers,
        trips: filteredTripsJS,
      })) ||
    [];

  const { recommendedTrips } = userPreferences.toJS();
  const recommendedTripsForSearch = !isRecommendedTripsEnabled()
    ? []
    : getRecommendedTripsListBySearch({
        searchId,
        recommendedTrips,
        providerId,
      });
  const promoUrgencyTrips = filteredTripsJS;

  if (Object.keys(recommendedTripsForSearch).length) {
    filteredTripsJS = getRecommendedTripsExcludeList({
      recommendedTripsList: recommendedTripsForSearch,
      tripList: filteredTripsJS,
    });
  }

  const originId = search.get('originId');
  const destinationId = search.get('destinationId');
  const originTerminal = terminals[originId];
  const destinationTerminal = terminals[destinationId];

  // Get terminal info from the origin and destination terminals
  const tripTerminalInfo = getTripTerminalInfo({ originTerminal, destinationTerminal });

  const analyticsSearchData = getSearchAnalyticsData({
    search,
    places,
    departures: trips.get(searchId),
    returns: returnSearchId ? trips.get(returnSearchId) : null,
  }).search;

  // Get closed trips from backend response
  const closedTrips = trips.getIn([searchId, 'buses', 'closedTrips']);
  const closedTripsJs = closedTrips?.toJS() ?? [];

  // After-midnight section. Like closedTrips it deliberately skips `filterTrips`, so the user's
  // filters and sort never reach it — it is a separate block, not part of the listing. It is
  // already ordered by departure upstream.
  //
  // It IS scoped to the active provider, with the same predicate the main listing uses
  // (`providerFilter`, on `providerId`) and NOT `matchesTripProvider`, which compares `lineId`.
  // Those diverge on copyright-protected lines, where `parseTrip` sets `providerId` to the
  // redirection URL — and provider tabs are keyed by `providerId`, so `matchesTripProvider` would
  // filter the section out of exactly the page where the main listing is shown.
  const afterMidnightWindowFrom = trips.getIn([searchId, 'buses', 'afterMidnight', 'windowFrom']);
  const afterMidnightTripList = trips.getIn([searchId, 'buses', 'afterMidnight', 'trips']);
  const afterMidnightTrips = afterMidnightTripList
    ? afterMidnightTripList
        .filter((trip) => !providerId || trip.get('providerId') === providerId)
        .toJS()
    : [];

  return {
    // Part of the after-midnight impression key. It is computed above for the store lookups, but it
    // was not being handed down: without it the guard keyed on `undefined` and two searches whose
    // first small-hours trip leaves at the same time shared one key, silently dropping the second
    // impression.
    searchId,
    activeDepartureFilter,
    activeLocationFilter,
    activeStopsFilter,
    departureDate,
    noTrips,
    passengers,
    providerName,
    returnDate,
    searchIsPolling,
    sortBy,
    transportType,
    hasDynamicPromotion,
    openTrip,
    stopsFilter,
    categoriesFilter,
    tripOptionsFilter,
    arrivalLocationFilter: aLocationFilter,
    currentPath: location.pathname,
    departureFilter: departureTimeFilter,
    departureLocationFilter: dLocationFilter,
    destination: places[search.getIn(['departure', 'destinationId'])],
    paymentPlans: paymentPlansList || [],
    installmentsMinAmount: config.installmentsMinAmount,
    locations: tripsLocationByProvider(tripList, providerId),
    origin: places[search.getIn(['departure', 'originId'])],
    provider: provider && provider.toJS(),
    providers: providerList ? providerList.toJS() : [],
    roundTrip,
    sortProvidersBy: providers.get('sortBy'),
    tooManyFilters: hasTooManyFilters({
      searchIsPolling,
      noTrips,
      activeFilters,
      brand: env.brand,
      tripList,
    }),
    trips: filteredTripsJS,
    recommendedTrips: recommendedTripsForSearch,
    promoUrgencyTrips,
    closedTrips: closedTripsJs,
    afterMidnightTrips,
    afterMidnightWindowFrom,
    tripSortVisible: overlayView.get('tripSortVisible'),
    tripDetails: visibleTrip ? parseTripDetails(visibleTrip.toJS()) : null,
    isStopsFilterAvailable: isStopsFilterAvailable(tripList),
    couponCode,
    discountedTripsByCoupon,
    paintsListQuote: paintsQuote,
    isOpenTicketList: Boolean(isOpen),
    isLogged: Boolean(siemprePlus.get('user')),
    providerId,
    areOpenTicketTrips,
    filters: filters.toJS(),
    tripsWithoutFilters,
    nearTerminals,
    discountPercent,
    operationNumbers,
    nit,
    resultPriceToShow: search.get('resultPriceToShow'),
    firstTripLineId: tripsForLineInfo?.lineId,
    firstTripLineLogo: tripsForLineInfo?.line?.logoUrl,
    specialDiscountVariant,
    showSpecialDiscountBanner,
    brand: env.brand,
    dateFormat: env.searchWidget?.dateFormat,
    passengersNotFound,
    tripTerminalInfo,
    passengersQueryParam: passengers,
    analyticsSearchData,
    departureIsOpenTicket: queryDepartureIsOpenTicket,
    purchaseToken: purchase.get('token'),
    purchaseLoaded: purchase.get('loaded'),
    purchaseIsFetching: purchase.get('isFetching'),
  };
};

export const mapDispatchToProps = (dispatch, ownProps) => ({
  showModal: (component, extraProps) => dispatch(toggleModal('visible', component, extraProps)),
  setSortOrder: (sortBy, list) =>
    dispatch(list === 'providers' ? setProviderSortOrder(sortBy) : setTripSortOrder(sortBy)),
  setTripFilter: (filterBy, active) => dispatch(setTripFilter(filterBy, active)),
  setTripFilters: (filter) => dispatch(setTripFilters(filter)),
  resetTripFilters: () => dispatch(resetTripFilters()),
  transitionTo: (route, searchQuery) => {
    const queryString = stringifyQueryString(searchQuery, ownProps.location.search);
    ownProps.history.push(`${route}${queryString}`);
  },
  replaceUrl: (route) => ownProps.history.replace(route),
  closeTripDetails: () => dispatch(showTripDetails(null)),
  setRedirectParams: (way, params, brand, redirect, roundTrip, waitForPurchase) =>
    dispatch(setRedirectParams(way, params, brand, redirect, roundTrip, waitForPurchase)),
  goBack: () => {
    ownProps.history.go(-1);
  },
  fetchTripsDetails: (trips) => dispatch(fetchTripsDetails(trips)),
  resetSeatBus: () => dispatch(resetSeatBus()),
  newPurchase: (originSlug, seenPrice) =>
    dispatch(newPurchase(originSlug, undefined, 'A1', seenPrice, null, false, {}, true, null, {})),
  createQuickPurchase: ({
    departureId,
    returnId,
    passengersParam = 'A1',
    seenPrice,
    selectedSeatReferences,
  }) => {
    const references = cloneSelectedSeatReferences(selectedSeatReferences);
    const preSelectedSeats = references?.length
      ? { preSelectedSeats: { departure: references } }
      : undefined;
    return dispatch(
      newPurchase(
        departureId,
        returnId || undefined,
        passengersParam,
        seenPrice,
        undefined,
        false,
        {},
        false,
        '',
        undefined,
        preSelectedSeats,
        true,
      ),
    );
  },
  fetchQuickPurchaseByToken: (token) => dispatch(fetchQuickPurchaseByToken(token)),
});

export default compose(
  withTranslation('trips'),
  connect(mapStateToProps, mapDispatchToProps),
)(ProviderDeparturesComponent);
